import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { InventoryReflectionTypes, InventoryReflectionTypesEnum } from "../shared/constants";
import CustomerDropDown from "../shared/CustomerDropDown";
import DropDown from "../shared/dropdown";
import HotelsDropDown from "../shared/HotelsDropDown";
import RatesDropDown from "../shared/RatesDropDown";
import PromotionsDropDown from "../shared/PromotionsDropDown";
import RoomTypesDropDown from "../shared/RoomTypesDropDown";
import TextInput from "../shared/TextInput";
import WingDropDown from "../shared/WingDropDown";
import { useMessageResources, useTextResources } from "../../language";
import PropTypes from "prop-types";
import { onSuccess, onWarning, onError } from "../shared/messages";
import { useFormFieldLabel } from "../../hooks/useFormFieldLabel";
import EntityTypes from "../../pages/GroupReservation/Constants/EntityTypes";
import { isNonEmptyArray } from "../shared/utils";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { GetWings } from "../../api/services/organization";
import { AllocationAndStrategy, RoomTypes } from "../../api/services/next"
import { RecalculateAvailabilityBySpecification } from "../../api/services/pms";
import CheckBox from "../shared/Checkbox";
import { SyncChannelManagerProducts } from "../../api/services/pms";
import CustomerGroupDropDown from "../shared/CustomerGroupDropDown";
import { useConfirm } from "../../hooks/useConfirm";

export const AllocationCreationPopup = ({ extendedProps: { addRow } = { addRow: () => { } },
  open, onCancelChanges, onApplyChanges, updatedAllocation }) => {
  const currentHotel = useCurrentHotel();
  const text = useTextResources();
  const message = useMessageResources();
  const label = useFormFieldLabel();
  const confirm = useConfirm();
  const [allocation, setAllocation] = useState({});
  const [initalStateAllocation, setInitalStateAllocation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isPerReservation, setIsPerReservation] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const changeAllocationField = useCallback((fieldName, value) => {
    setAllocation((prevState) => ({ ...prevState, [fieldName]: value }), []);
    setIsDirty(true);
  }, []);

  const toggleAllocationField = useCallback((fieldName) => {
    setAllocation((prevState) => ({ ...prevState, [fieldName]: !prevState[fieldName] }), []);
    setIsDirty(true);
  }, []);

  const setName = useCallback((value) => changeAllocationField("Name", value), [changeAllocationField]);
  const setWing = useCallback((value) => changeAllocationField("WingId", value), [changeAllocationField]);
  const setRateIds = useCallback((values) => changeAllocationField("RateIds", values), [changeAllocationField]);
  const setPromotionIds = useCallback((values) => changeAllocationField("PromotionIds", values), [changeAllocationField]);
  const setRoomTypeIds = useCallback((values) => changeAllocationField("RoomTypeIds", values), [changeAllocationField]);
  const setCustomerIds = useCallback((customersIds) => changeAllocationField("CustomerIds", customersIds), [changeAllocationField]);
  const setCustomerGroupIds = useCallback((customerGroupIds) => changeAllocationField("CustomerGroupIds", customerGroupIds), [changeAllocationField]);
  const setInventoryReflectionId = useCallback((value) => changeAllocationField("InventoryReflectionId", value), [changeAllocationField]);
  const toggleConsiderOverbooking = useCallback(() => toggleAllocationField("ConsiderOverbooking"), [toggleAllocationField]);
  const toggleDistribute = useCallback(() => toggleAllocationField("Distribute"), [toggleAllocationField]);

  const handleHotelChanged = useCallback((value) => {
    setAllocation((prevState) => ({
      ...prevState,
      HotelId: value,
      RoomTypeIds: [],
      RateIds: [],
    }));
  }, []);

  const handleSharedInventoryChanged = useCallback((value) => {
    setAllocation((prevState) => {
      const state = {
        ...prevState,
        IsSharedInventory: value,
      };
      if (value) {
        state.RoomTypeIds = [];
        state.isHotelLevel = false;
      }
      return state;
    });
  }, []);

  const handleHotelLevelChanged = useCallback((value) => {
    setAllocation((prevState) => {
      const state = {
        ...prevState,
        RoomTypeIds: [],
        WingId: null,
        isHotelLevel: value,
      };
      if (value) {
        state.IsSharedInventory = false;
      }
      return state;
    });
  }, []);


  const getMissingFields = useCallback((allocationObj) => {
    const missingFields = [];
    if (!allocationObj.Name)
      missingFields.push(text("AllocationName"));
    if (!allocationObj.HotelId)
      missingFields.push(text("Hotel"));
    if (!allocationObj.InventoryReflectionId)
      missingFields.push(text("InventoryReflection"));
    // Add "Customers" to missing fields if both CustomerIds and CustomerGroupIds are not non-empty arrays
    if (!isNonEmptyArray(allocationObj.CustomerIds) && !isNonEmptyArray(allocationObj.CustomerGroupIds))
      missingFields.push(text("Customers"));
    // At least one of Rates or Promotions must be selected
    if (!isNonEmptyArray(allocationObj.RateIds) && !isNonEmptyArray(allocationObj.PromotionIds)) {
      missingFields.push(`${text("Rates")} ${text("Or")} ${text("Promotions")}`);
    }
    if (!allocationObj.isHotelLevel) {
      if (!allocationObj.WingId)
        missingFields.push(text("Wing"));
      if (!isNonEmptyArray(allocationObj.RoomTypeIds))
        missingFields.push(text("RoomTypes"));
    }
    return missingFields;
  }, [text]);

  const baseSpec = useMemo(() => ({ HotelId: allocation.HotelId }), [allocation.HotelId]);

  useEffect(() => {
    if (updatedAllocation && open) {
      setIsPerReservation(updatedAllocation.IsPerReservation);

      setIsUpdate(updatedAllocation?.Id > 0);
      setAllocation({ ...updatedAllocation, isHotelLevel: updatedAllocation?.Id > 0 && !updatedAllocation?.WingId });
      setInitalStateAllocation(updatedAllocation);
    }
    else setAllocation({});
  }, [open, updatedAllocation]);

  useEffect(() => {
    if (allocation.HotelId && !isUpdate) {
      GetWings({ HotelId: allocation.HotelId }).then(res => {
        // Auto-select if there's exactly one wing available (create mode only)
        if (res.length === 1) {
          changeAllocationField("WingId", res[0].Id);
        }
      });
    }
  }, [allocation.HotelId, isUpdate, changeAllocationField]);


  const onSave = useCallback(async () => {
    const missingFields = getMissingFields(allocation);
    if (missingFields.length !== 0) {
      onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
      return false;
    }

    try {
      setIsLoading(true);

      const createEntity = (entityId, entityTypeId, allocationId) => ({
        EntityId: entityId,
        EntityTypeId: entityTypeId,
        AllocationId: allocationId,
      });

      const allocationToPost = { ...allocation };

      // Ensure WingId is null when hotelLevel is true
      if (allocationToPost.isHotelLevel) {
        allocationToPost.WingId = null;
      }

      allocationToPost.AllocationEntities = [
        ...(allocation.CustomerIds || []).map(customerId =>
          createEntity(customerId, EntityTypes.Customer, allocation.Id)),
        ...(allocation.CustomerGroupIds || []).map(customerGroupId =>
          createEntity(customerGroupId, EntityTypes.CustomerGroup, allocation.Id)),
        ...(allocation.RateIds || []).map(rateId =>
          createEntity(rateId, EntityTypes.Rate, allocation.Id)),
        ...(allocation.RoomTypeIds || []).map(roomTypeId =>
          createEntity(roomTypeId, EntityTypes.RoomType, allocation.Id)),
        ...(allocation.PromotionIds || []).map(promotionId =>
          createEntity(promotionId, EntityTypes.Promotion, allocation.Id))
      ];

      allocationToPost.AllocationEntities = allocationToPost.AllocationEntities.filter(entity => entity.EntityId);

      if (isUpdate) {
        await AllocationAndStrategy.UpdateAllocation(allocationToPost);

        if (allocationToPost.IsSharedInventory && allocationToPost.MinDate && allocationToPost.MaxDate) {
          await RecalculateAvailabilityBySpecification({
            HotelID: allocationToPost.HotelId,
            StartDate: allocationToPost.MinDate,
            EndDate: allocationToPost.MaxDate,
            RoomTypeIDs: updatedAllocation.RoomTypeIds,
            AllocationIDs: [allocationToPost.Id]
          });
        }

        if (allocationToPost.Distribute && allocationToPost.MinDate && allocationToPost.MaxDate) {

          var roomTypesForDistribution = allocation.HotelLevelAllocation ?
            (await RoomTypes.GetRoomTypes(roomTypesSpec)).map(rt => rt.Id) :
            allocation.RoomTypeIds || [];

          roomTypesForDistribution = Array.from(
            new Set([
              ...roomTypesForDistribution,
              ...(initalStateAllocation?.RoomTypeIds || [])
            ])
          );

          var rateIdsForDistribution = Array.from(
            new Set([
              ...allocation.RateIds || [],
              ...(initalStateAllocation?.RateIds || [])
            ])
          );

          await SyncChannelManagerProducts({
            HotelID: allocationToPost.HotelId,
            RoomTypeIds: roomTypesForDistribution,
            RateIds: rateIdsForDistribution,
            StartDate: allocationToPost.MinDate,
            EndDate: allocationToPost.MaxDate,
            IsUpdateAvailabilty: true,
            RecalculateAvailabilty: true,
            Reason: `Allocation properties updated, Allocation ID: ${allocationToPost.Id}`,
            TriggerOperation: "Allocation Update"
          })
        }
      }
      else {
        await AllocationAndStrategy.AddAllocation(allocationToPost);
      }

      await addRow();
      setIsDirty(false);
      onSuccess();
      return true;
    } catch (error) {
      error.status === 400 || error.error?.detail === text("AllocationStrategyExists") ?
        onWarning(error) : onError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getMissingFields, allocation, text, isUpdate, addRow, updatedAllocation?.RoomTypeIds, message]);

  const saveAndClose = useCallback(async () => {
    const ok = await onSave();
    if (ok) {
      onApplyChanges?.();
      onCancelChanges?.();
    }
  }, [onSave, onApplyChanges, onCancelChanges]);

  const handleClose = useCallback(async () => {
    if (isDirty) {
      if (await confirm({
        content: `${message("WouldYouLikeToContinue")}?`,
        confirmButton: {
          content: text('Yes'),
          positive: true,
        },
        cancelButton: {
          content: text('No'),
          negative: true,
        },
      }))
        onCancelChanges?.();
    }
    else onCancelChanges?.();
  }, [isDirty, text, message, onCancelChanges, confirm]);

  const roomTypesSpec = useMemo(() => ({
    HotelId: allocation.HotelId,
    RateIds: allocation.RateIds,
    PromotionIds: allocation.PromotionIds
  }), [allocation.HotelId, allocation.RateIds, allocation.PromotionIds]);

  const ratesSpec = useMemo(() => ({
    ExcludeAddonRates: true,
    HotelId: allocation.HotelId,
    RoomTypeIds: allocation.RoomTypeIds
  }), [allocation.HotelId, allocation.RoomTypeIds]);

  const promotionSpec = useMemo(() => ({
    HotelId: allocation.HotelId,
    RoomTypeIds: allocation.RoomTypeIds
  }), [allocation.HotelId, allocation.RoomTypeIds]);

  const hotelsSpec = useMemo(() => ({
    HotelChainId: currentHotel?.HotelChainId
  }), [currentHotel?.HotelChainId]);

  const getHeaderTitle = () => {
    if (isUpdate) {
      return `${text("Update")} ${text(isPerReservation ? "AllocationPerReservation" : "AllocationPerRoomNights")}`
    }
    else {
      return `${text(isPerReservation ? "CreateAllocationPerReservation" : "CreateAllocationPerRoomNights")}`
    }
  }

  return (
    <Modal size="large" dimmer centered open={open} onClose={handleClose}>
      <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {getHeaderTitle()} <Button circular icon="close" onClick={handleClose} />
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field
            variant="outlined"
            control={TextInput}
            label={text("AllocationName")}
            value={allocation?.Name || updatedAllocation?.Name}
            onChange={setName}
            required
          />
          <Form.Group widths="equal">
            <Form.Field
              fluid
              variant="outlined"
              label={text("Hotel")}
              required
              disabled={isUpdate}
              control={HotelsDropDown}
              onChange={handleHotelChanged}
              value={allocation.HotelId}
              spec={hotelsSpec}
              search
            />
            {!allocation.isHotelLevel && (<Form.Field
              fluid
              variant="outlined"
              label={text("Wing")}
              disabled={isUpdate}
              required
              value={allocation.WingId}
              control={WingDropDown}
              onChange={setWing}
              HotelID={allocation.HotelId}
              search
            />)}
          </Form.Group>
          {!isPerReservation && <Form.Group widths="equal">
            <Form.Field
              control={CheckBox}
              label={text("HotelLevelAllocation")}
              disabled={isUpdate}
              onChange={handleHotelLevelChanged}
              checked={Boolean(allocation.isHotelLevel)}
            />
            <Form.Field
              control={CheckBox}
              disabled={isUpdate}
              label={label("SharedInventory", "SharedInventory")}
              onChange={handleSharedInventoryChanged}
              checked={Boolean(allocation.IsSharedInventory)}
            />
          </Form.Group>}
          <Form.Field
            label={text("Rates")}
            placeholder={text("Select")}
            control={RatesDropDown}
            value={allocation.RateIds}
            multiple
            clearable
            required
            onChange={setRateIds}
            spec={ratesSpec}
            search
          />
          <Form.Field
            label={text("Promotions")}
            placeholder={text("Select")}
            control={PromotionsDropDown}
            value={allocation.PromotionIds}
            multiple
            clearable
            required
            onChange={setPromotionIds}
            spec={promotionSpec}
            search
          />
          {!allocation.isHotelLevel && (
            <Form.Field
              label={text("RoomTypes")}
              placeholder={text("Select")}
              control={RoomTypesDropDown}
              value={allocation.RoomTypeIds}
              multiple
              clearable
              onChange={setRoomTypeIds}
              spec={roomTypesSpec}
              required
              search
            />)}
          <Form.Field
            label={text("Customers")}
            placeholder={text("Select")}
            control={CustomerDropDown}
            value={allocation.CustomerIds}
            multiple
            onChange={setCustomerIds}
            required
            clearable
            spec={baseSpec}
          />
          <Form.Field
            label={text("CustomerGroups")}
            placeholder={text("Select")}
            control={CustomerGroupDropDown}
            value={allocation.CustomerGroupIds}
            multiple
            onChange={setCustomerGroupIds}
            required
            clearable
          />
          {!isPerReservation && <Form.Group widths="equal" className="flex-center">
            <Form.Field
              label={label("InventoryReflection", "InventoryReflection")}
              disabled={isUpdate}
              value={allocation.InventoryReflectionId}
              placeholder={text("Select")}
              control={({ ...rest }) => (
                <DropDown data={allocation.IsSharedInventory || allocation.isHotelLevel ?
                  InventoryReflectionTypes.slice(1).map((x) => ({ Name: text(x.Name), Id: x.Id })) :
                  InventoryReflectionTypes.map((x) => ({ Name: text(x.Name), Id: x.Id }))} {...rest}
                  textField="Name" valueField="Id" disabled={isUpdate} />
              )}
              required
              onChange={setInventoryReflectionId}
            />
            <Form.Field
              control={CheckBox}
              checked={Boolean(allocation.Distribute)}
              label={text("DistributeAllocations")}
              onChange={toggleDistribute}
            />
            <Form.Field
              control={CheckBox}
              checked={Boolean(allocation.ConsiderOverbooking)}
              label={text("ConsiderOverbooking")}
              onChange={toggleConsiderOverbooking}
              disabled={allocation.InventoryReflectionId === InventoryReflectionTypesEnum.NoInventoryReflection}
            />
          </Form.Group>}
        </Form>
        <Form.Group style={{ marginTop: 10 }}>
          <Form>
            <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button negative onClick={handleClose} disabled={isLoading}>
                {text('Close')}
              </Button>
              <Button positive onClick={saveAndClose} loading={isLoading} disabled={isLoading}>
                {text("Saveandclose")}
              </Button>
              <Button positive onClick={onSave} loading={isLoading} disabled={isLoading}>
                {text("Save")}
              </Button>
            </Modal.Actions>
          </Form>
        </Form.Group>
      </Modal.Content>
    </Modal>
  );
};

AllocationCreationPopup.propTypes = {
  extendedProps: PropTypes.object,
  open: PropTypes.bool,
  onCancelChanges: PropTypes.func,
  onApplyChanges: PropTypes.func,
  updatedAllocation: PropTypes.object
};
